'use client'

import { useEffect } from 'react'

// Anti-fraud behavioral measurement. Fires a `humanity_signal` event with a
// score 0-5 based on real human signals observed during the visit:
//  - mousemove detected (desktop indicator)
//  - scroll detected
//  - keydown detected
//  - touchstart detected (mobile indicator)
//  - time on page > 3 seconds
//
// This analytics event respects the visitor's analytics consent.
export function HumanityTracker() {
  useEffect(() => {
    let mouseDetected = false
    let scrollDetected = false
    let keyboardDetected = false
    let touchDetected = false
    const startTime = Date.now()
    let reported = false

    const onMouse = () => {
      mouseDetected = true
    }
    const onScroll = () => {
      scrollDetected = true
    }
    const onKey = () => {
      keyboardDetected = true
    }
    const onTouch = () => {
      touchDetected = true
    }

    window.addEventListener('mousemove', onMouse, { once: true, passive: true })
    window.addEventListener('scroll', onScroll, { once: true, passive: true })
    window.addEventListener('keydown', onKey, { once: true, passive: true })
    window.addEventListener('touchstart', onTouch, { once: true, passive: true })

    function report() {
      try {
        if (localStorage.getItem('cookie-consent') !== 'all') return
      } catch {
        return
      }
      if (reported) return
      reported = true
      const timeOnPage = Date.now() - startTime
      const humanityScore =
        (mouseDetected ? 1 : 0) +
        (scrollDetected ? 1 : 0) +
        (keyboardDetected ? 1 : 0) +
        (touchDetected ? 1 : 0) +
        (timeOnPage > 3000 ? 1 : 0)

      try {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'humanity_signal',
          humanity_score: humanityScore,
          humanity_mouse: mouseDetected,
          humanity_scroll: scrollDetected,
          humanity_keyboard: keyboardDetected,
          humanity_touch: touchDetected,
          humanity_duration_ms: timeOnPage,
        })
      } catch {
        // Analytics must never break the app
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') report()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    // pagehide is the most reliable cross-browser unload signal,
    // especially on iOS Safari which often skips beforeunload.
    window.addEventListener('pagehide', report)
    // beforeunload as an additional desktop safety net.
    window.addEventListener('beforeunload', report)

    return () => {
      window.removeEventListener('mousemove', onMouse)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('touchstart', onTouch)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', report)
      window.removeEventListener('beforeunload', report)
    }
  }, [])

  return null
}
